<template>
  <div class="main-layout" :class="{ 'music-auth-layout': shouldHideHeader, 'home-layout': homeLayoutActive }">
    <!-- Home 弥散背景：与音乐列表同款，从主题 BGPIC 提取主题色 -->
    <ImusicMeshBackground
      v-if="homeLayoutActive"
      :source-image-url="homeMeshBackgroundSourceUrl"
      :theme-mode="currentThemeMode"
      variant="page"
    />
    <!-- PC 端轻微蒙版，与 music-list 保持一致 -->
    <div
      v-if="homeLayoutActive && isDesktopViewport"
      class="home-background-overlay"
      aria-hidden="true"
    />

    <!-- 在特定路由下不渲染header -->
    <header
      v-if="!shouldHideHeader"
      class="header"
      :class="{ 'header-hidden': isHeaderHidden, 'home-header': homeLayoutActive }"
      @mouseenter="handleHeaderMouseEnter"
      @mouseleave="handleHeaderMouseLeave"
    >
      <div class="logo" style="display: none;"></div>
      <div class="nav-right">
        <nav class="nav">
          <router-link 
            v-for="item in visibleNavItems" 
            :key="item.path" 
            :to="item.path"
            class="nav-item"
          >
            {{ item.name }}
          </router-link>
        </nav>
        <div class="user-nav">
          <a v-if="!isLoggedIn" href="#" class="login-link" @click.prevent="triggerLogin">登录</a>
          <div v-else class="user-info" @click="toggleUserDropdown" @mouseenter="openUserDropdown">
            <span class="username">{{ username }}</span>
            <div v-if="userStore.isAdmin" class="admin-badge">管</div>
            <transition name="menu-fade">
              <div
                v-if="isUserDropdownOpen"
                class="user-dropdown"
                @mouseenter="handleUserDropdownMouseEnter"
                @mouseleave="handleUserDropdownMouseLeave"
                @click.stop
              >
                <!-- <a href="#" @click.prevent="goToUserCenter">个人中心</a> -->
                <div class="dropdown-link dropdown-link-with-status" @click="switchThemeMode">
                  <span>主题切换</span>
                  <span class="dropdown-link-status">{{ currentThemeLabel }}</span>
                </div>
                <router-link v-if="userStore.isAdmin" to="/admin" class="dropdown-link">后台管理</router-link>
                <div
                  v-if="userStore.hasDevTools"
                  class="dropdown-link"
                  @click="openDevOptionsModal"
                >开发选项</div>
                <div class="dropdown-link" @click="openLogUploadModal">问题反馈</div>
                <div class="dropdown-separator"></div>
                <a href="#" class="dropdown-link dropdown-link-logout" @click.prevent="logout">退出登录</a>
                <!-- <a href="#" @click.prevent="refreshUserInfo">刷新用户状态</a> -->
              </div>
            </transition>
          </div>
        </div>
      </div>
    </header>

    <a
      v-if="!shouldHideHeader"
      href="#"
      class="feedback-pill"
      :class="{ 'home-feedback-pill': homeLayoutActive, 'header-hidden': isHeaderHidden }"
      @click.prevent="openLogUploadModal"
    >问题反馈</a>
    
    <main :class="['main-content', homeLayoutActive ? 'home-view' : '', $route.path.includes('admin') ? 'admin-view' : '', $route.path.startsWith('/imusic') ? 'imusic' : '', $route.path === '/todo' ? 'todo-view' : '', $route.path.includes('bead-pattern') ? 'pindou-view' : '']">
      <div 
        class="content-container" 
        :class="{ 'no-margin': $route.path.startsWith('/imusic') || $route.path === '/todo', 'pindou-container': $route.path.includes('bead-pattern') }"
        :style="contentContainerStyle"
      >
        <router-view @scroll-event="handleScrollEvent" v-slot="{ Component, route }">
          <transition name="fade" mode="out-in">
            <component :is="Component" :key="route.path" />
          </transition>
        </router-view>
      </div>
    </main>
    
    <!-- 🔍 全局元素选择器组件 -->
    <ElementInspector :enabled="isElementInspectorEnabled" @close="closeElementInspector" />

    <!-- 全局登录/注册弹窗 -->
    <AuthModal />
    <ModuleActivationModal />
    <LogUploadModal v-model:visible="showLogUploadModal" />

    <transition name="theme-modal-fade">
      <div
        v-if="showDevOptionsModal"
        class="theme-modal-overlay"
        @click="closeDevOptionsModal"
      >
        <div class="theme-modal player-settings-modal" @click.stop>
          <div class="theme-modal-header">
            <h3>开发选项</h3>
          </div>
          <div class="theme-option-list">
            <div class="theme-option player-style-option dev-option-row">
              <span class="player-style-option-text">
                <span class="player-style-option-title">开发模式</span>
                <span class="player-style-option-desc">开启后使用 DXHPlayer</span>
              </span>
              <div class="ios-toggle-switch" @click.stop="toggleDevMode">
                <div class="ios-toggle-switch-track" :class="{ active: isDevModeEnabled }">
                  <div class="ios-toggle-switch-thumb" :class="{ active: isDevModeEnabled }"></div>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="theme-option player-style-option"
              @click="openParticleTextEffectTest"
            >
              <span class="player-style-option-text">
                <span class="player-style-option-title">粒子特效测试</span>
                <span class="player-style-option-desc">文字消散 / 凝聚效果页</span>
              </span>
            </button>
            <button
              type="button"
              class="theme-option player-style-option"
              @click="openLatticePlaybackTest"
            >
              <span class="player-style-option-text">
                <span class="player-style-option-title">Lattice 播放界面</span>
                <span class="player-style-option-desc">用当前账号的歌词版歌曲试 Folia Lattice</span>
              </span>
            </button>
            <button
              type="button"
              class="theme-option player-style-option"
              @click="openAiImagePixelTest"
            >
              <span class="player-style-option-text">
                <span class="player-style-option-title">AI 绘图测试</span>
                <span class="player-style-option-desc">AI 像素风生图 / 参数对比页</span>
              </span>
            </button>
            <button
              type="button"
              class="theme-option player-style-option"
              @click="openWordLyricsTest"
            >
              <span class="player-style-option-text">
                <span class="player-style-option-title">逐字歌词测试</span>
                <span class="player-style-option-desc">按歌名/歌手查询 AMLLDB 逐字歌词</span>
              </span>
            </button>
            <button
              type="button"
              class="theme-option player-style-option"
              @click="openMusicAccountTest"
            >
              <span class="player-style-option-text">
                <span class="player-style-option-title">音乐账号测试</span>
                <span class="player-style-option-desc">五平台扫码登录 / 播放 / 逐字歌词</span>
              </span>
            </button>
          </div>
          <div class="menu-separator"></div>
          <div class="theme-modal-actions">
            <button class="theme-cancel-button" @click="closeDevOptionsModal">取消</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useUserStore } from '@/store/user';
import { defineComponent, computed, onMounted, onUnmounted, watch, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import ElementInspector from '@/components/devtools/ElementInspector.vue';
import globalBackButtonService from '@/services/GlobalBackButtonService.js';
import fullscreenEventService from '@/services/FullscreenEventService.js';
import { useAuthModalStore } from '@/store/authModal';
import AuthModal from '@/components/auth/AuthModal.vue';
import ModuleActivationModal from '@/components/auth/ModuleActivationModal.vue';
import LogUploadModal from '@/components/common/LogUploadModal.vue';
import ImusicMeshBackground from '@/components/music/ImusicMeshBackground.vue';
import defaultBGPIC from '@/assets/images/background/BGPIC.jpg';
import defaultBGPICPC from '@/assets/images/background/BGPIC-PC.jpg';
import blueThemeBGPIC from '@/assets/images/background/蓝色/BGPIC.jpg';
import blueThemeBGPICPC from '@/assets/images/background/蓝色/BGPIC-PC.jpg';
import greenThemeBGPIC from '@/assets/images/background/绿色/BGPIC.jpg';
import greenThemeBGPICPC from '@/assets/images/background/绿色/BGPIC-PC.jpg';
import grayThemeBGPIC from '@/assets/images/background/灰色/BGPIC.jpg';
import grayThemeBGPICPC from '@/assets/images/background/灰色/BGPIC-PC.jpg';
import purpleThemeBGPIC from '@/assets/images/background/紫色/BGPIC.jpg';
import purpleThemeBGPICPC from '@/assets/images/background/紫色/BGPIC-PC.jpg';
import cyanThemeBGPIC from '@/assets/images/background/青色/BGPIC.jpg';
import cyanThemeBGPICPC from '@/assets/images/background/青色/BGPIC-PC.jpg';
import yellowThemeBGPIC from '@/assets/images/background/黄色/BGPIC.jpg';
import yellowThemeBGPICPC from '@/assets/images/background/黄色/BGPIC-PC.jpg';
import blackThemeBGPIC from '@/assets/images/background/黑色/BGPIC.jpg';
import blackThemeBGPICPC from '@/assets/images/background/黑色/BGPIC-PC.jpg';

const THEME_STORAGE_KEY = 'imusic_theme_mode';
const DEV_MODE_STORAGE_KEY = 'imusic_dev_mode_enabled';
const THEME_PINK = 'pink';
const THEME_BLUE = 'blue';
const THEME_GREEN = 'green';
const THEME_GRAY = 'gray';
const THEME_PURPLE = 'purple';
const THEME_BLACK = 'black';
const THEME_CYAN = 'cyan';
const THEME_YELLOW = 'yellow';
const VALID_THEME_MODES = new Set([THEME_PINK, THEME_BLUE, THEME_GREEN, THEME_CYAN, THEME_YELLOW, THEME_GRAY, THEME_PURPLE, THEME_BLACK]);

const DEFAULT_THEME_MODE = THEME_PINK;
/** 与 router-view 的 fade 过渡时长一致，离开首页时短暂保留首页布局，避免内容被 margin 顶下去 */
const ROUTE_FADE_MS = 150;

const loadThemeModeFromStorage = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return VALID_THEME_MODES.has(stored) ? stored : DEFAULT_THEME_MODE;
  } catch (error) {
    console.error('❌ [主题] 读取 localStorage 失败:', error);
    return DEFAULT_THEME_MODE;
  }
};

const getThemeBackgrounds = (themeMode) => {
  if (themeMode === THEME_BLUE) {
    return {
      mobile: blueThemeBGPIC,
      pc: blueThemeBGPICPC
    };
  }
  if (themeMode === THEME_GREEN) {
    return {
      mobile: greenThemeBGPIC,
      pc: greenThemeBGPICPC
    };
  }
  if (themeMode === THEME_CYAN) {
    return {
      mobile: cyanThemeBGPIC,
      pc: cyanThemeBGPICPC
    };
  }
  if (themeMode === THEME_YELLOW) {
    return {
      mobile: yellowThemeBGPIC,
      pc: yellowThemeBGPICPC
    };
  }
  if (themeMode === THEME_GRAY) {
    return {
      mobile: grayThemeBGPIC,
      pc: grayThemeBGPICPC
    };
  }
  if (themeMode === THEME_PURPLE) {
    return {
      mobile: purpleThemeBGPIC,
      pc: purpleThemeBGPICPC
    };
  }
  if (themeMode === THEME_BLACK) {
    return {
      mobile: blackThemeBGPIC,
      pc: blackThemeBGPICPC
    };
  }
  return {
    mobile: defaultBGPIC,
    pc: defaultBGPICPC
  };
};

export default defineComponent({
  name: 'MainLayout',
  components: {
    ElementInspector,
    AuthModal,
    ModuleActivationModal,
    LogUploadModal,
    ImusicMeshBackground
  },
  setup() {
    const userStore = useUserStore();
    const router = useRouter();
    const route = useRoute();
    const authModal = useAuthModalStore();
    
    
    // 添加导航栏隐藏状态
    const isHeaderHidden = computed(() => {
      // 在/imusic路由下始终隐藏导航栏
      return route.path === '/imusic';
    });
    
    // 延迟隐藏header的状态（用于拼豆图生成器页面的动画延迟）
    const delayedHideHeader = ref(false);
    let hideHeaderTimer = null;
    
    // 控制是否隐藏整个header
    const shouldHideHeader = computed(() => {
      // 隐藏header的路由规则：
      // 1. 所有以 /imusic 开头的路由（音乐业务模块）
      // 2. /music-auth 路由（音乐认证页面）
      // 3. 路由meta中设置了 hideHeader: true 的路由（如短链接跳转页面、待办事项页面）
      // 4. 拼豆图生成器页面（需要延迟隐藏）
      const shouldHideForIMusic = route.path.startsWith('/imusic');
      const shouldHideForAuth = route.path === '/music-auth';
      const shouldHideForMeta = route.meta?.hideHeader === true;
      const shouldHideForPinDou = route.path.includes('/bead-pattern-generator') || route.path.includes('/pindou') || route.path.includes('拼豆');
      
      // 对于拼豆图生成器页面，需要等待延迟状态
      if (shouldHideForPinDou) {
        return delayedHideHeader.value;
      }
      
      const shouldHide = shouldHideForIMusic || shouldHideForAuth || shouldHideForMeta;
      
      console.log('当前路由:', route.path, '是否隐藏header:', shouldHide, {
        isIMusicRoute: shouldHideForIMusic,
        isAuthRoute: shouldHideForAuth,
        isMetaHide: shouldHideForMeta,
        isPinDouRoute: shouldHideForPinDou,
        delayedHide: delayedHideHeader.value
      });
      
      return shouldHide;
    });
    
    const navItems = [
      { name: '首页', path: '/' }
    ];
    
    // 根据用户权限和当前路由过滤导航项
    const visibleNavItems = computed(() => {
      console.log('计算可见导航项，当前用户角色:', userStore.userRole);
      console.log('是否为管理员:', userStore.isAdmin);
      console.log('当前路由:', route.path);
      
      return navItems.filter(item => {
        // 如果是首页链接，且当前就在首页，则不显示
        if (item.path === '/' && route.path === '/') {
          return false;
        }
        
        // 如果需要管理员权限，检查用户是否为管理员
        if (item.requireAdmin) {
          const isVisible = userStore.isLoggedIn && userStore.isAdmin;
          console.log('管理员导航项可见性:', isVisible);
          return isVisible;
        }
        
        // 否则正常显示
        return true;
      });
    });
    
    const username = computed(() => userStore.username);
    const isLoggedIn = computed(() => userStore.isLoggedIn);
    
    // 全屏状态管理
    const isFullscreenMode = ref(false);
    const fullscreenComponent = ref('');
    
    // 🎨 自适应缩放状态
    const adaptiveScale = ref(1);
    const adaptiveScaleEnabled = ref(false);
    
    // 🔍 元素选择器状态
    const ELEMENT_INSPECTOR_STORAGE_KEY = 'imusic_element_inspector_enabled';
    const isElementInspectorEnabled = ref(false);
    
    const showLogUploadModal = ref(false);
    const showDevOptionsModal = ref(false);
    const loadDevModeFromStorage = () => {
      try {
        const stored = localStorage.getItem(DEV_MODE_STORAGE_KEY);
        if (stored === null) {
          return true;
        }
        return stored === 'true';
      } catch (error) {
        console.error('❌ [开发模式] 读取 localStorage 失败:', error);
        return true;
      }
    };
    const isDevModeEnabled = ref(loadDevModeFromStorage());
    const currentThemeMode = ref(loadThemeModeFromStorage());
    const themeCycleOrder = [THEME_PINK, THEME_BLUE, THEME_GREEN, THEME_CYAN, THEME_YELLOW, THEME_GRAY, THEME_PURPLE, THEME_BLACK];
    const currentThemeLabel = computed(() => {
      if (currentThemeMode.value === THEME_BLUE) return '蓝色';
      if (currentThemeMode.value === THEME_GREEN) return '绿色';
      if (currentThemeMode.value === THEME_CYAN) return '青色';
      if (currentThemeMode.value === THEME_YELLOW) return '黄色';
      if (currentThemeMode.value === THEME_GRAY) return '灰色';
      if (currentThemeMode.value === THEME_PURPLE) return '紫色';
      if (currentThemeMode.value === THEME_BLACK) return '黑色';
      return '粉色';
    });
    
    // 用户下拉菜单状态
    const isUserDropdownOpen = ref(false);
    const isPointerInHeader = ref(false);
    const isPointerInDropdown = ref(false);
    let userDropdownCloseTimer = null;

    const canUseHoverInteraction = () => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
      }
      return window.matchMedia('(hover: hover)').matches;
    };

    const clearUserDropdownCloseTimer = () => {
      if (userDropdownCloseTimer) {
        clearTimeout(userDropdownCloseTimer);
        userDropdownCloseTimer = null;
      }
    };

    const scheduleUserDropdownAutoClose = () => {
      if (!isUserDropdownOpen.value) {
        return;
      }

      clearUserDropdownCloseTimer();
      userDropdownCloseTimer = setTimeout(() => {
        if (!isPointerInHeader.value && !isPointerInDropdown.value) {
          isUserDropdownOpen.value = false;
        }
        userDropdownCloseTimer = null;
      }, 300);
    };
    
    // 打开用户下拉菜单（鼠标悬停时，仅在非触摸设备上生效）
    const openUserDropdown = () => {
      if (!canUseHoverInteraction()) {
        return;
      }
      isPointerInHeader.value = true;
      clearUserDropdownCloseTimer();
      isUserDropdownOpen.value = true;
    };
    
    // 关闭用户下拉菜单（鼠标移出时）
    const closeUserDropdown = () => {
      clearUserDropdownCloseTimer();
      isPointerInDropdown.value = false;
      isUserDropdownOpen.value = false;
    };
    
    // 切换用户下拉菜单（点击时）
    const toggleUserDropdown = (event) => {
      event.stopPropagation();
      isUserDropdownOpen.value = !isUserDropdownOpen.value;
      if (isUserDropdownOpen.value) {
        clearUserDropdownCloseTimer();
      } else {
        clearUserDropdownCloseTimer();
        isPointerInDropdown.value = false;
      }
    };

    const handleHeaderMouseEnter = () => {
      if (!canUseHoverInteraction()) {
        return;
      }
      isPointerInHeader.value = true;
      clearUserDropdownCloseTimer();
    };

    const handleHeaderMouseLeave = (event) => {
      if (!canUseHoverInteraction()) {
        return;
      }

      isPointerInHeader.value = false;
      const relatedTarget = event?.relatedTarget;
      const movingToDropdown = relatedTarget && typeof relatedTarget.closest === 'function' && relatedTarget.closest('.user-dropdown');
      if (movingToDropdown) {
        return;
      }

      if (!isPointerInDropdown.value) {
        scheduleUserDropdownAutoClose();
      }
    };

    const handleUserDropdownMouseEnter = () => {
      isPointerInDropdown.value = true;
      clearUserDropdownCloseTimer();
    };

    const handleUserDropdownMouseLeave = (event) => {
      isPointerInDropdown.value = false;
      const relatedTarget = event?.relatedTarget;
      const movingToHeader = relatedTarget && typeof relatedTarget.closest === 'function' && relatedTarget.closest('.header');
      if (movingToHeader) {
        return;
      }

      if (!isPointerInHeader.value) {
        scheduleUserDropdownAutoClose();
      }
    };

    const switchThemeMode = () => {
      const oldTheme = currentThemeMode.value;
      const currentIndex = themeCycleOrder.indexOf(oldTheme);
      const nextTheme = themeCycleOrder[(currentIndex + 1) % themeCycleOrder.length];
      currentThemeMode.value = nextTheme;

      try {
        localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
        window.dispatchEvent(new StorageEvent('storage', {
          key: THEME_STORAGE_KEY,
          newValue: nextTheme,
          oldValue: oldTheme,
          storageArea: localStorage
        }));
      } catch (error) {
        console.error('❌ [主题] 保存到 localStorage 失败:', error);
      }
    };
    
    // 点击外部关闭下拉菜单
    const handleClickOutside = (event) => {
      if (isUserDropdownOpen.value) {
        clearUserDropdownCloseTimer();
        isPointerInDropdown.value = false;
        isUserDropdownOpen.value = false;
      }
    };
    
    // 从 localStorage 加载元素选择器状态
    const loadElementInspectorState = () => {
      try {
        const stored = localStorage.getItem(ELEMENT_INSPECTOR_STORAGE_KEY);
        isElementInspectorEnabled.value = stored === 'true';
      } catch (error) {
        console.error('❌ [元素选择器] 读取 localStorage 失败:', error);
      }
    };
    
    // 监听 localStorage 变化（用于跨组件通信）
    const handleStorageChange = (event) => {
      if (event.key === ELEMENT_INSPECTOR_STORAGE_KEY) {
        isElementInspectorEnabled.value = event.newValue === 'true';
      } else if (event.key === THEME_STORAGE_KEY) {
        currentThemeMode.value = VALID_THEME_MODES.has(event.newValue) ? event.newValue : DEFAULT_THEME_MODE;
      }
    };
    
    // 关闭元素选择器
    const closeElementInspector = () => {
      isElementInspectorEnabled.value = false;
      try {
        localStorage.setItem(ELEMENT_INSPECTOR_STORAGE_KEY, 'false');
      } catch (error) {
        console.error('❌ [元素选择器] 保存到 localStorage 失败:', error);
      }
    };
    
    // 自适应缩放样式计算
    const adaptiveScaleStyle = computed(() => {
      // 全屏模式下不应用缩放
      if (isFullscreenMode.value) {
        return {};
      }
      
      // 拼豆图 / 后台管理不应用缩放（避免影响 fixed / 分栏滚动）
      if (route.path.includes('bead-pattern') || route.path.includes('/admin')) {
        return {};
      }
      
      // 如果未启用缩放，返回空样式
      if (!adaptiveScaleEnabled.value) {
        return {};
      }
      
      // 应用缩放transform
      return {
        transform: `scale(${adaptiveScale.value})`,
        transformOrigin: 'top center',
        transition: 'transform 0.3s ease'
      };
    });

    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0);

    const isHomeRoute = computed(() => route.path === '/');

    const preserveHomeLeaveLayout = ref(false);
    let preserveHomeLeaveLayoutTimer = null;

    const clearPreserveHomeLeaveLayoutTimer = () => {
      if (preserveHomeLeaveLayoutTimer) {
        clearTimeout(preserveHomeLeaveLayoutTimer);
        preserveHomeLeaveLayoutTimer = null;
      }
    };

    const homeLayoutActive = computed(() => isHomeRoute.value || preserveHomeLeaveLayout.value);

    const isDesktopViewport = computed(() => windowWidth.value >= 768);

    /** Home 弥散背景来源图 — 与 music-list 的 timeBackgroundUrl 同源 */
    const homeMeshBackgroundSourceUrl = computed(() => {
      const width = windowWidth.value || (typeof window !== 'undefined' ? window.innerWidth : 0);
      const { mobile, pc } = getThemeBackgrounds(currentThemeMode.value);
      return width >= 768 ? pc : mobile;
    });

    const contentContainerStyle = computed(() => ({
      ...adaptiveScaleStyle.value
    }));
    
    // 监听CSS变量变化，更新自适应缩放状态
    const updateAdaptiveScale = () => {
      const scaleValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--adaptive-scale')
        .trim();
      const enabledValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--adaptive-scale-enabled')
        .trim();
      
      if (scaleValue) {
        adaptiveScale.value = parseFloat(scaleValue) || 1;
      }
      adaptiveScaleEnabled.value = enabledValue === '1';
    };
    
    // 处理来自子组件的滚动事件
    const handleScrollEvent = (data) => {
      // 在imusic路由下不处理滚动事件，因为header已经隐藏
      if (route.path.startsWith('/imusic')) {
        return;
      }
      // 只在其他音乐页面处理滚动事件
      if (route.path.includes('music')) {
        isHeaderHidden.value = data.hideHeader;
      }
    };
    
    // 剪切板检测现在由新的检测机制自动处理
    // 用户交互监听器和粘贴事件监听器会自动检测抖音链接

// 全屏事件处理函数
const handleFullscreenEnter = (data) => {
  console.log('🎬 [MainLayout] 收到全屏进入事件:', data);
  isFullscreenMode.value = true;
  fullscreenComponent.value = data.componentName;
  
  // 注意：样式已经通过FullscreenEventService直接应用到DOM，这里只需要更新状态
  console.log('🎬 [MainLayout] 全屏模式已激活，样式已通过直接DOM操作应用');
};

const handleFullscreenExit = (data) => {
  console.log('🎬 [MainLayout] 收到全屏退出事件:', data);
  isFullscreenMode.value = false;
  fullscreenComponent.value = '';
  
  // 注意：样式已经通过FullscreenEventService直接恢复，这里只需要更新状态
  console.log('🎬 [MainLayout] 全屏模式已退出，样式已通过直接DOM操作恢复');
};

    // 当组件挂载时检查登录状态
    onMounted(async () => {
      console.log('MainLayout组件挂载，检查登录状态');
      
      // 初始化延迟隐藏header状态（如果当前路由是拼豆图生成器页面，立即隐藏）
      const currentPath = route.path;
      const isPinDouRoute = currentPath.includes('/bead-pattern-generator') || currentPath.includes('/pindou') || currentPath.includes('拼豆');
      if (isPinDouRoute) {
        delayedHideHeader.value = true;
      }
      
      // 🔍 初始化元素选择器状态
      loadElementInspectorState();
      window.addEventListener('storage', handleStorageChange);
      
      // 添加全局点击监听器，用于关闭下拉菜单
      document.addEventListener('click', handleClickOutside);
      
      // 🎨 初始化自适应缩放状态
      updateAdaptiveScale();
      
      // 监听窗口大小变化，更新自适应缩放与 Home 弥散背景来源图
      const resizeObserver = new ResizeObserver(() => {
        windowWidth.value = window.innerWidth;
        updateAdaptiveScale();
      });
      resizeObserver.observe(document.documentElement);
      
      // 监听CSS变量变化（通过MutationObserver）
      const styleObserver = new MutationObserver(() => {
        updateAdaptiveScale();
      });
      styleObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style']
      });
      
      // 清理函数
      onUnmounted(() => {
        resizeObserver.disconnect();
        styleObserver.disconnect();
      });
      
      // 调试：检查页面加载时的localStorage状态
      console.log('🔍 [调试] 页面加载时的localStorage状态:', {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
        loginTimestamp: localStorage.getItem('loginTimestamp'),
        isLoggedIn: localStorage.getItem('isLoggedIn'),
        userRole: localStorage.getItem('userRole')
      });
      
      
      // 初始化全局返回键处理服务
      console.log('🔙 [全局返回键] 在MainLayout中初始化全局返回键处理服务...');
      globalBackButtonService.init();
      
      // 初始化全屏事件监听
      console.log('🎬 [全屏事件] 在MainLayout中初始化全屏事件监听...');
      fullscreenEventService.on('fullscreen-enter', handleFullscreenEnter);
      fullscreenEventService.on('fullscreen-exit', handleFullscreenExit);
      
      // 根据当前路由设置页面类型
      const currentRoute = route.path;
      if (currentRoute.startsWith('/imusic')) {
        globalBackButtonService.setCurrentPageType('imusic');
      } else if (currentRoute.includes('music')) {
        globalBackButtonService.setCurrentPageType('music');
      } else if (currentRoute.includes('admin')) {
        globalBackButtonService.setCurrentPageType('admin');
      } else {
        globalBackButtonService.setCurrentPageType('default');
      }
      
      // 只有在本地存储中有登录标记时才尝试获取用户信息
      if (localStorage.getItem('isLoggedIn') === 'true') {
        console.log('🔍 [调试] 检测到isLoggedIn=true，尝试获取用户信息');
        try {
          await userStore.fetchCurrentUser();
          
          // 用户登录成功后，剪切板检测会自动处理
          if (userStore.isLoggedIn && (currentRoute.includes('music') || currentRoute.startsWith('/imusic'))) {
            console.log('📋 [剪切板检测] 用户已登录且在音乐页面，剪切板检测已自动启动');
          }
        } catch (error) {
          // 已在store中处理了错误，这里不需要额外处理
        }
      } else {
        console.info('游客模式浏览');
      }
    });
    
    // 监听路由变化，重置导航栏状态
    watch(() => route.path, async (newPath, oldPath) => {
      // 清除之前的定时器
      if (hideHeaderTimer) {
        clearTimeout(hideHeaderTimer);
        hideHeaderTimer = null;
      }
      clearPreserveHomeLeaveLayoutTimer();
      if (oldPath === '/' && newPath !== '/') {
        preserveHomeLeaveLayout.value = true;
        preserveHomeLeaveLayoutTimer = setTimeout(() => {
          preserveHomeLeaveLayout.value = false;
          preserveHomeLeaveLayoutTimer = null;
        }, ROUTE_FADE_MS);
      } else if (newPath === '/') {
        preserveHomeLeaveLayout.value = false;
      }
      
      // 检查是否是拼豆图生成器页面
      const isPinDouRoute = newPath.includes('/bead-pattern-generator') || newPath.includes('/pindou') || newPath.includes('拼豆');
      const wasPinDouRoute = oldPath && (oldPath.includes('/bead-pattern-generator') || oldPath.includes('/pindou') || oldPath.includes('拼豆'));
      
      // 如果离开拼豆图生成器页面，立即显示header
      if (wasPinDouRoute && !isPinDouRoute) {
        delayedHideHeader.value = false;
      }
      
      // 如果进入拼豆图生成器页面，延迟隐藏header（等待路由过渡动画完成）
      if (isPinDouRoute && !wasPinDouRoute) {
        // 路由过渡动画时间是0.15s，延迟相同时间后再隐藏header
        hideHeaderTimer = setTimeout(() => {
          delayedHideHeader.value = true;
          hideHeaderTimer = null;
        }, ROUTE_FADE_MS);
      }
      
      // 更新页面类型
      if (newPath.startsWith('/imusic')) {
        globalBackButtonService.setCurrentPageType('imusic');
      } else if (newPath.includes('music')) {
        globalBackButtonService.setCurrentPageType('music');
      } else if (newPath.includes('admin')) {
        globalBackButtonService.setCurrentPageType('admin');
      } else {
        globalBackButtonService.setCurrentPageType('default');
      }
      
      // 离开音乐页面时重置导航栏状态
      // 排除imusic相关路由，因为它们不需要header
      if (!newPath.includes('music') && 
          route.name !== 'IMusic' && 
          !newPath.startsWith('/imusic')) {
        isHeaderHidden.value = false;
      }
      
      // 当用户进入音乐相关页面且已登录时，剪切板检测会自动处理
      if (userStore.isLoggedIn && (newPath.includes('music') || newPath.startsWith('/imusic'))) {
        console.log('📋 [剪切板检测] 用户进入音乐页面，剪切板检测已自动启动');
      }
    });
    
    // 组件卸载时清理事件监听器
    onUnmounted(() => {
      console.log('🎬 [全屏事件] 清理全屏事件监听器...');
      fullscreenEventService.off('fullscreen-enter', handleFullscreenEnter);
      fullscreenEventService.off('fullscreen-exit', handleFullscreenExit);
      window.removeEventListener('storage', handleStorageChange);
      
      // 清理全局点击监听器
      document.removeEventListener('click', handleClickOutside);
      
      // 清理延迟隐藏header的定时器
      if (hideHeaderTimer) {
        clearTimeout(hideHeaderTimer);
        hideHeaderTimer = null;
      }
      clearPreserveHomeLeaveLayoutTimer();

      clearUserDropdownCloseTimer();
    });
    
    const triggerLogin = () => {
      // 导航到首页并带上 auth=login，Home 下会显示全局弹窗
      if (route.path !== '/') {
        router.push({ path: '/', query: { auth: 'login' } })
      } else {
        authModal.open('login')
        router.replace({ path: route.path, query: { ...route.query, auth: 'login' } })
      }
    }

    // 刷新用户信息方法
    const refreshUserInfo = async () => {
      try {
        const userData = await userStore.fetchCurrentUser();
        if (userData) {
          console.log('获取当前用户信息成功:', userData);
          console.log('登录状态:', userStore.isLoggedIn);
          console.log('用户角色:', userStore.userRole);
          console.log('是否为管理员:', userStore.isAdmin);
          message.success('用户信息已刷新');
        } else {
          console.info('未登录状态，无法刷新用户信息');
        }
      } catch (error) {
        // 已在store中处理了错误
      }
    };
    
    // 登出方法
    const logout = async () => {
      try {
        await userStore.userLogout();
        
        // 无论在什么页面，退出登录后都返回首页
        router.push('/');
      } catch (error) {
        console.error('登出失败:', error);
      }
    };
    
    const goToUserCenter = () => {
      router.push('/user');
    };

    const openLogUploadModal = () => {
      isUserDropdownOpen.value = false;
      showLogUploadModal.value = true;
    };

    const openDevOptionsModal = () => {
      isUserDropdownOpen.value = false;
      isDevModeEnabled.value = loadDevModeFromStorage();
      showDevOptionsModal.value = true;
    };

    const closeDevOptionsModal = () => {
      showDevOptionsModal.value = false;
    };

    const toggleDevMode = () => {
      isDevModeEnabled.value = !isDevModeEnabled.value;
      try {
        localStorage.setItem(DEV_MODE_STORAGE_KEY, String(isDevModeEnabled.value));
      } catch (error) {
        console.error('❌ [开发模式] 保存到 localStorage 失败:', error);
      }
    };

    const openParticleTextEffectTest = () => {
      showDevOptionsModal.value = false;
      router.push('/dev/particle-text-effect');
    };

    const openLatticePlaybackTest = () => {
      showDevOptionsModal.value = false;
      router.push('/dev/lattice-playback');
    };

    const openAiImagePixelTest = () => {
      showDevOptionsModal.value = false;
      router.push('/ai-image-pixel-test');
    };

    const openWordLyricsTest = () => {
      showDevOptionsModal.value = false;
      router.push('/dev/word-lyrics');
    };

    const openMusicAccountTest = () => {
      showDevOptionsModal.value = false;
      router.push('/dev/music-account');
    };

    return {
      navItems,
      visibleNavItems,
      isLoggedIn,
      username,
      logout,
      goToUserCenter,
      refreshUserInfo,
      userStore,
      isHeaderHidden,
      shouldHideHeader,
      handleScrollEvent,
      isElementInspectorEnabled,
      closeElementInspector,
      contentContainerStyle,
      isHomeRoute,
      homeLayoutActive,
      isDesktopViewport,
      homeMeshBackgroundSourceUrl,
      currentThemeMode,
      triggerLogin,
      isUserDropdownOpen,
      toggleUserDropdown,
      handleHeaderMouseEnter,
      handleHeaderMouseLeave,
      handleUserDropdownMouseEnter,
      handleUserDropdownMouseLeave,
      switchThemeMode,
      openUserDropdown,
      closeUserDropdown,
      currentThemeLabel,
      showLogUploadModal,
      openLogUploadModal,
      showDevOptionsModal,
      isDevModeEnabled,
      openDevOptionsModal,
      closeDevOptionsModal,
      toggleDevMode,
      openParticleTextEffectTest,
      openLatticePlaybackTest,
      openAiImagePixelTest,
      openWordLyricsTest,
      openMusicAccountTest
    };
  }
});
</script>

<style lang="scss" scoped>
.main-layout {
  min-height: 100vh; /* Fallback for browsers that do not support Custom Properties */
  min-height: calc(var(--vh, 1vh) * 100); /* 🔥 iPad Safari兼容：使用动态视口高度，考虑工具栏 */
  display: flex;
  flex-direction: column;
  background-color: var(--bg-color-light);
  
  /* 纯色背景 */
  background-color: #f5f5f7;
  background-image: none;
}

.main-layout.home-layout {
  background-color: transparent;
}

.home-background-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(var(--vh, 1vh) * 100);
  background: rgba(255, 255, 255, 0.02);
  z-index: 1;
  pointer-events: none;
}

.header {
  height: 48px;
  padding: 0 0px;
  border: 1px solid rgba(255, 255, 255, 0.62);
  background:
    linear-gradient(180deg, rgba(245, 245, 245, 0.9) 0%, rgba(235, 235, 235, 0.82) 100%),
    linear-gradient(140deg, rgba(255, 255, 255, 0.86) 0%, rgba(226, 229, 235, 0.5) 100%);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  box-shadow: 
    0 18px 52px rgba(9, 14, 22, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.74),
    inset 0 -1px 0 rgba(178, 187, 201, 0.36);
  
  border-radius: 99px;
  display: inline-flex;
  align-items: center;
  gap: 16px;
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 1000;
  transition: transform 0.3s ease, box-shadow 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.3s ease;
  white-space: nowrap;
  isolation: isolate;
  
  /* 硬件加速优化 */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.header.home-header {
  height: 38px;
  border: 1px solid transparent;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(18px) saturate(108%);
  -webkit-backdrop-filter: blur(18px) saturate(108%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -1px 0 rgba(255, 255, 255, 0.12);
}

.header.home-header::before {
  inset: 1px;
  background: rgba(255, 255, 255, 0.02);
}

.header.home-header::after {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: inherit;
  pointer-events: none;
  z-index: 2;
  background: conic-gradient(
    from 45deg,
    rgba(255, 255, 255, 0.42) 0deg,
    rgba(255, 255, 255, 0) 45deg,
    rgba(255, 255, 255, 0.36) 120deg,
    rgba(255, 255, 255, 0.4) 180deg,
    rgba(255, 255, 255, 0) 225deg,
    rgba(255, 255, 255, 0.36) 300deg,
    rgba(255, 255, 255, 0.42) 360deg
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

.header.home-header:hover {
  border-color: transparent;
  background: rgba(255, 255, 255, 0.2);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.65),
    inset 0 -1px 0 rgba(255, 255, 255, 0.16);
}

.header.home-header:active {
  background: rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1);
}

.header.home-header .login-link {
  color: #FFFFFF;
}

.header.home-header .login-link:hover {
  color: #FFFFFF;
}

.header.home-header .username {
  color: #FFFFFF;
}

.header::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background:
    radial-gradient(120% 70% at 10% 0%, rgba(255, 255, 255, 0.45) 0%, transparent 55%),
    radial-gradient(90% 60% at 100% 100%, rgba(255, 255, 255, 0.22) 0%, transparent 65%);
  z-index: 0;
}

.header > * {
  position: relative;
  z-index: 1;
}

.header.header-hidden {
  transform: translateY(-100%);
}

/* 悬停效果 - 参考迷你播放条 */
.header:hover {
  border-color: rgba(255, 255, 255, 0.68);
  background:
    linear-gradient(180deg, rgba(245, 245, 245, 0.94) 0%, rgba(235, 235, 235, 0.86) 100%),
    linear-gradient(140deg, rgba(255, 255, 255, 0.9) 0%, rgba(226, 229, 235, 0.56) 100%);
  box-shadow: 
    0 20px 56px rgba(9, 14, 22, 0.26),
    inset 0 1px 0 rgba(255, 255, 255, 0.78),
    inset 0 -1px 0 rgba(178, 187, 201, 0.4);
}

.logo {
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 0px;
  margin-left: auto;
  padding: 0px 5px;
}

.nav {
  display: flex;
  align-items: center;
  gap: 20px;

}

.nav-item {
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 700;
  transition: color 0.3s ease;
  height: 32px;
  display: flex;
  align-items: center;

  padding-left: 10px;
  padding-right: 5px;
  
  &:hover, &.router-link-active {
    color: var(--primary-color);
  }
}

.main-content {
  margin-top: 0;
  flex: 1;
  padding: 0;
  background-color: transparent; /* 改为透明，让 main-layout 的背景纹理显示出来 */
  
  /* 🎨 自适应缩放支持：确保缩放后的内容能够正常显示 */
  /* 当内容缩放时，main-content需要能够容纳缩放后的尺寸 */
  overflow-x: hidden;
  overflow-y: auto;
}

/* 在/imusic路由下移除顶部margin */
.main-content.imusic {
  margin-top: 0;
}

/* 首页去掉顶部间距，避免顶部露白 */
.main-content.home-view {
  margin-top: 0;
  position: relative;
  z-index: 2;
}

/* 待办事项页面全屏显示 */
.main-layout.music-auth-layout .main-content {
  margin-top: 0 !important;
  padding: 0 !important;
}

/* 待办事项页面样式 */
.main-content.todo-view {
  margin-top: 0;
  padding: 0;
  background-color: #1C1C1E;
}

/* 拼豆图页面样式 - 移除滚动容器，让 fixed 元素正确固定在视口 */
.main-content.pindou-view {
  overflow-y: visible !important;
  overflow-x: visible !important;
}

/* 后台管理：外层不滚动，由 AdminLayout 右侧内容区单独滚动，侧栏 fixed 固定 */
.main-content.admin-view {
  overflow: hidden !important;
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
  min-height: 0;
  flex: 0 0 auto;
}

.admin-view .content-container {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden !important;
  will-change: auto;
}

.main-layout:has(.admin-test) {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}

/* 后台移动端：允许双指缩放后拖动查看（配合 adminMobileViewport.js 放宽 viewport） */
html.admin-mobile-zoom-enabled {
  touch-action: auto;
  -ms-touch-action: auto;
}

html.admin-mobile-zoom-enabled .main-layout:has(.admin-test) {
  height: auto;
  min-height: 100vh;
  max-height: none;
  overflow: visible;
}

html.admin-mobile-zoom-enabled .main-content.admin-view {
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
  min-height: calc(100vh - 48px);
}

html.admin-mobile-zoom-enabled .admin-view .content-container {
  overflow: visible !important;
  max-height: none !important;
  height: auto !important;
}

html.admin-mobile-zoom-enabled .admin-test,
html.admin-mobile-zoom-enabled .admin-layout {
  height: auto !important;
  min-height: calc(100vh - 48px);
  overflow: visible !important;
}

html.admin-mobile-zoom-enabled .admin-content {
  overflow: visible !important;
  height: auto !important;
}

.content-container {
  max-width: 100%;
  margin: 0px auto;
  background-color: var(--bg-color);

  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  /* 🎨 自适应缩放通过内联样式动态应用 */
  /* 缩放时使用will-change优化性能 */
  will-change: transform;
}

/* 在/imusic路由下移除所有margin */
.content-container.no-margin {
  margin: 0;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  background-color: transparent;
  max-width: 100%;
}

/* 拼豆图页面的容器 - 移除 overflow 和 will-change，让 fixed 元素正确固定 */
.content-container.pindou-container {
  overflow: visible !important;
  will-change: auto !important;
}

/* 根据路由路径设置不同的容器宽度 */
.admin-view .content-container {
  max-width: 100%;
  margin: 0;
  border-radius: 0;
  box-shadow: none;
}

/* 首页：弥散背景由 ImusicMeshBackground 承担，容器保持透明 */
.home-view .content-container {
  background-color: transparent;
  box-shadow: none;
  position: relative;
  isolation: isolate;
}

.home-view .content-container > * {
  position: relative;
  z-index: 2;
}

@media (max-width: 768px) {
  .header {
    height: 42px;
  }
}

/* 🔥 全屏模式样式 - 临时取消390px宽度限制 */
body.fullscreen-mode .content-container {
  max-width: 100% !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background-color: transparent !important;
}

/* PC端和pad端全屏模式特殊处理 */
body.fullscreen-mode.device-desktop .content-container,
body.fullscreen-mode.device-tablet .content-container,
body.fullscreen-mode.device-mobile .content-container {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important; /* Fallback for browsers that do not support Custom Properties */
  height: calc(var(--vh, 1vh) * 100) !important; /* 动态可视高度，兼容移动端浏览器工具栏 */
  max-height: calc(var(--vh, 1vh) * 100) !important;
  max-width: none !important;
  margin: 0 !important;
  transform: none !important;
  z-index: 10000 !important;
  overflow: hidden !important;
}

.user-nav {
  position: relative;
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;
  height: 32px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.username {
  margin-right: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-color);
}

.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 160px;
  max-width: calc(100vw - 36px);
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.62);
  background:
    linear-gradient(180deg, rgba(245, 245, 245, 0.9) 0%, rgba(235, 235, 235, 0.82) 100%),
    linear-gradient(140deg, rgba(255, 255, 255, 0.86) 0%, rgba(226, 229, 235, 0.5) 100%);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  box-shadow: 
    0 18px 52px rgba(9, 14, 22, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.74),
    inset 0 -1px 0 rgba(178, 187, 201, 0.36);
  z-index: 1000;
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(120% 70% at 10% 0%, rgba(255, 255, 255, 0.45) 0%, transparent 55%),
      radial-gradient(90% 60% at 100% 100%, rgba(255, 255, 255, 0.22) 0%, transparent 65%);
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  .dropdown-link {
    display: flex;
    align-items: center;
    min-height: 38px;
    padding: 8px 12px;
    border-radius: 12px;
    cursor: pointer;
    transition: background-color 0.18s ease, transform 0.18s ease;
    color: #1a1a1a;
    font-size: 15px;
    font-weight: 520;
    line-height: 1.3;
    letter-spacing: 0.01em;
    text-decoration: none;

    &:hover {
      background: rgba(255, 255, 255, 0.44);
    }

    &:active {
      background: rgba(220, 226, 235, 0.55);
      transform: scale(0.985);
    }
  }

  .dropdown-separator {
    height: 1px;
    margin: 6px 8px 4px;
    background: linear-gradient(
      90deg,
      rgba(123, 133, 148, 0) 0%,
      rgba(123, 133, 148, 0.52) 14%,
      rgba(123, 133, 148, 0.52) 86%,
      rgba(123, 133, 148, 0) 100%
    );
    border-radius: 999px;
  }

  .dropdown-link-logout {
    color: #b42318;
    font-weight: 580;

    &:hover {
      background: rgba(255, 59, 48, 0.14);
    }

    &:active {
      background: rgba(255, 59, 48, 0.2);
    }
  }

  .dropdown-link-with-status {
    justify-content: space-between;
  }

  .dropdown-link-status {
    margin-left: auto;
    font-size: 12px;
    color: rgba(52, 60, 72, 0.72);
    font-weight: 560;
  }
}

.login-link {
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 16px;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 700;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.feedback-pill {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  height: 48px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.62);
  border-radius: 99px;
  background:
    linear-gradient(180deg, rgba(245, 245, 245, 0.9) 0%, rgba(235, 235, 235, 0.82) 100%),
    linear-gradient(140deg, rgba(255, 255, 255, 0.86) 0%, rgba(226, 229, 235, 0.5) 100%);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  box-shadow:
    0 18px 52px rgba(9, 14, 22, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.74),
    inset 0 -1px 0 rgba(178, 187, 201, 0.36);
  color: var(--primary-color);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  isolation: isolate;
  transition: transform 0.3s ease, box-shadow 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.3s ease;
}

.feedback-pill.home-feedback-pill {
  height: 38px;
  padding: 0 2px;
  border: none;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow: none;
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
  transition: color 0.2s ease;
}

.feedback-pill.home-feedback-pill:hover {
  color: #FFFFFF;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.feedback-pill.header-hidden {
  transform: translateY(-100%);
}

@media (max-width: 768px) {
  .feedback-pill {
    height: 42px;
  }

  .feedback-pill.home-feedback-pill {
    height: 38px;
  }
}

.admin-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  background-color: #f56c6c;
  color: white;
  border-radius: 50%;
  margin-right: 0px;
}

/* 音乐认证页面全屏显示 */
.main-layout {
  &.music-auth-layout {
    .main-content {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    
    .content-container {
      margin: 0 !important;
      padding: 0 !important;
      background-color: transparent;
      box-shadow: none;
      min-height: 100vh; /* Fallback for browsers that do not support Custom Properties */
      min-height: calc(var(--vh, 1vh) * 100); /* 🔥 iPad Safari兼容：使用动态视口高度，考虑工具栏 */
    }
  }
}

/* 菜单动画 */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.22, 0.61, 0.36, 1);
  transform-origin: top right;
}

.menu-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) translateX(3px) scale(0.97);
}

.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) translateX(2px) scale(0.98);
}

/* 路由过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.theme-modal-fade-enter-active,
.theme-modal-fade-leave-active {
  transition: opacity 0.24s ease;
}

.theme-modal-fade-enter-from,
.theme-modal-fade-leave-to {
  opacity: 0;
}

.theme-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 16, 22, 0.22);
  backdrop-filter: blur(12px) saturate(115%);
  -webkit-backdrop-filter: blur(12px) saturate(115%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
}

.theme-modal {
  position: relative;
  width: 260px;
  max-width: calc(100vw - 36px);
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.62);
  background:
    linear-gradient(180deg, rgba(245, 245, 245, 0.62) 0%, rgba(235, 235, 235, 0.46) 100%),
    linear-gradient(140deg, rgba(255, 255, 255, 0.54) 0%, rgba(226, 229, 235, 0.24) 100%);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  box-shadow:
    0 18px 52px rgba(9, 14, 22, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.74),
    inset 0 -1px 0 rgba(178, 187, 201, 0.36);
  overflow: hidden;
  isolation: isolate;
}

.theme-modal::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(120% 70% at 10% 0%, rgba(255, 255, 255, 0.45) 0%, transparent 55%),
    radial-gradient(90% 60% at 100% 100%, rgba(255, 255, 255, 0.22) 0%, transparent 65%);
  z-index: 0;
}

.theme-modal > * {
  position: relative;
  z-index: 1;
}

.theme-modal-header {
  padding: 8px 12px 4px;
}

.theme-modal-header h3 {
  margin: 0 !important;
  color: #1a1a1a;
  font-size: 15px;
  font-weight: 580;
  letter-spacing: 0.01em;
  width: 100%;
  text-align: center;
}

.theme-option-list {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.theme-option {
  width: 100%;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 520;
  line-height: 1.3;
  letter-spacing: 0.01em;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.theme-option:hover {
  background: rgba(255, 255, 255, 0.44);
}

.theme-option:active {
  background: rgba(220, 226, 235, 0.55);
  transform: scale(0.985);
}

.theme-modal-actions {
  padding: 2px 0 0;
}

.theme-cancel-button {
  width: 100%;
  border: none;
  border-radius: 12px;
  min-height: 38px;
  padding: 8px 12px;
  background: transparent;
  color: #b42318;
  font-size: 15px;
  font-weight: 580;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.theme-cancel-button:hover {
  background: rgba(255, 59, 48, 0.14);
}

.theme-cancel-button:active {
  background: rgba(255, 59, 48, 0.2);
  transform: scale(0.985);
}

.player-settings-modal {
  width: 280px;
}

.player-style-option {
  align-items: flex-start;
  min-height: 44px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.dev-option-row {
  justify-content: space-between;
  align-items: center;
  cursor: default;
}

.dev-option-row:hover {
  background: transparent;
}

.dev-option-row:active {
  transform: none;
  background: transparent;
}

.player-style-option-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.player-style-option-title {
  font-size: 15px;
  font-weight: 580;
  line-height: 1.25;
  color: #1a1a1a;
}

.player-style-option-desc {
  font-size: 12px;
  font-weight: 450;
  line-height: 1.3;
  color: rgba(26, 26, 26, 0.58);
}

.menu-separator {
  height: 1px;
  margin: 6px 8px 4px;
  background: linear-gradient(
    90deg,
    rgba(123, 133, 148, 0) 0%,
    rgba(123, 133, 148, 0.52) 14%,
    rgba(123, 133, 148, 0.52) 86%,
    rgba(123, 133, 148, 0) 100%
  );
  border-radius: 999px;
}

.ios-toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-left: 10px;
  flex-shrink: 0;
}

.ios-toggle-switch-track {
  position: relative;
  width: 37px;
  height: 20px;
  background-color: rgba(28, 28, 30, 0.18);
  border-radius: 26px;
  transition: background-color 0.3s ease;
  cursor: pointer;
}

.ios-toggle-switch-track.active {
  background-color: #34C759;
}

.ios-toggle-switch-thumb {
  position: absolute;
  top: 0px;
  left: 2px;
  width: 19px;
  height: 19px;
  background-color: #FFFFFF;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-1px);
}

.ios-toggle-switch-thumb.active {
  transform: translateX(15px);
}

.ios-toggle-switch:hover .ios-toggle-switch-track {
  background-color: rgba(28, 28, 30, 0.24);
}

.ios-toggle-switch:hover .ios-toggle-switch-track.active {
  background-color: #30D158;
}
</style>
